import { Component, EventEmitter, Input, Output, HostListener, ElementRef, ViewChild } from '@angular/core';

@Component({
  selector: 'ui-datepicker',
  templateUrl: './datepicker.component.html',
  standalone: false
})
export class UiDatepickerComponent {
  @Input() label = '';
  @Input() value = '';
  @Input() dateFormat = 'YYYY-MM-DD';
  @Input() compact = false;
  @Input() centerScreen = false;
  @Output() valueChange = new EventEmitter<string>();
  @ViewChild('trigger', { static: false }) trigger: ElementRef;

  open = false;
  viewDate: Date;
  days: (number | null)[] = [];
  monthLabel = '';
  openAbove = false;
  view: 'days' | 'months' | 'years' = 'days';
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  yearRange: number[] = [];
  popupStyle: Record<string, string> = {};

  constructor(private el: ElementRef) {}

  private justOpened = false;

  @HostListener('document:click', ['$event'])
  onDocClick(e: Event) {
    // Skip if we just opened (prevents immediate close)
    if (this.justOpened) {
      this.justOpened = false;
      return;
    }
    if (!this.el.nativeElement.contains(e.target)) {
      this.open = false;
    }
  }

  toggle() {
    this.open = !this.open;
    if (this.open) {
      this.justOpened = true;
    }
    if (this.open) {
      this.view = 'days';
      this.viewDate = this.value ? new Date(this.value + 'T00:00:00') : new Date();
      this.buildCalendar();
      this.detectPosition();
    }
  }

  showMonths() { this.view = 'months'; }

  showYears() {
    const y = this.viewDate.getFullYear();
    this.yearRange = [];
    const start = y - 5;
    for (let i = 0; i < 12; i++) this.yearRange.push(start + i);
    this.view = 'years';
  }

  selectMonth(m: number) {
    this.viewDate.setMonth(m);
    this.view = 'days';
    this.buildCalendar();
  }

  selectYear(y: number) {
    this.viewDate.setFullYear(y);
    this.view = 'months';
  }

  prevYearRange() { this.yearRange = this.yearRange.map(y => y - 12); }
  nextYearRange() { this.yearRange = this.yearRange.map(y => y + 12); }

  buildCalendar() {
    const y = this.viewDate.getFullYear();
    const m = this.viewDate.getMonth();
    this.monthLabel = this.viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    this.days = [];
    for (let i = 0; i < firstDay; i++) this.days.push(null);
    for (let d = 1; d <= daysInMonth; d++) this.days.push(d);
  }

  prevMonth() { this.viewDate.setMonth(this.viewDate.getMonth() - 1); this.buildCalendar(); }
  nextMonth() { this.viewDate.setMonth(this.viewDate.getMonth() + 1); this.buildCalendar(); }

  selectDay(day: number) {
    const y = this.viewDate.getFullYear();
    const m = String(this.viewDate.getMonth() + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    this.value = `${y}-${m}-${d}`;
    this.valueChange.emit(this.value);
    this.open = false;
  }

  today() { this.viewDate = new Date(); this.buildCalendar(); this.selectDay(new Date().getDate()); }
  clear() { this.value = ''; this.valueChange.emit(this.value); this.open = false; }

  isSelected(day: number): boolean {
    if (!day) return false;
    const y = this.viewDate.getFullYear();
    const m = String(this.viewDate.getMonth() + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return this.value === `${y}-${m}-${d}`;
  }

  get displayValue(): string {
    return this.value ? this.formatForDisplay(this.value) : 'Select date';
  }

  formatForDisplay(isoDate: string): string {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    switch (this.dateFormat) {
      case 'YYYY/MM/DD': return `${y}/${m}/${d}`;
      case 'DD.MM.YYYY': return `${d}.${m}.${y}`;
      case 'DD/MM/YYYY': return `${d}/${m}/${y}`;
      case 'MM/DD/YYYY': return `${m}/${d}/${y}`;
      default: return isoDate;
    }
  }

  private detectPosition(): void {
    // Center screen mode - used for modals/dialogs where relative positioning doesn't work
    if (this.centerScreen) {
      this.popupStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
      return;
    }

    // Default: position relative to trigger element
    const rect = this.el.nativeElement.getBoundingClientRect();
    const popupHeight = this.compact ? 280 : 320;
    const popupWidth = this.compact ? 240 : 260;

    // Determine if popup should open above or below
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // For compact mode or when not enough space below, open above
    this.openAbove = this.compact || (spaceBelow < popupHeight && spaceAbove > popupHeight);

    // Calculate left position - prefer aligning to trigger, but clamp to viewport
    let left = rect.left;
    
    // If popup would go off right edge, align to right edge of viewport
    if (left + popupWidth > window.innerWidth - 16) {
      left = window.innerWidth - popupWidth - 16;
    }
    
    // Ensure not off left edge
    if (left < 8) {
      left = 8;
    }

    if (this.openAbove) {
      // Position above the trigger
      const bottom = window.innerHeight - rect.top + 8;
      this.popupStyle = { left: left + 'px', bottom: bottom + 'px' };
    } else {
      // Position below the trigger
      this.popupStyle = { left: left + 'px', top: (rect.bottom + 8) + 'px' };
    }
  }
}
